<template>
  <!--start booking receipt page-->
  <v-container fluid fill-height>
    <v-progress-circular
      v-if="isLoading"
      indeterminate
      color="primary"
      size="128"
      class="mx-auto"
    />

    <v-card
      v-else
      tile
      elevation="0"
      :width="$vuetify.breakpoint.smAndDown ? '92%' : '700px'"
      class="pt-6 pb-8 mx-auto"
    >
      <v-btn
        outlined
        text
        dark
        color="indigo"
        elevation="0"
        @click="backToHome()"
      >
        <v-icon dark left> mdi-arrow-left </v-icon>
        <span class="caption">{{ $t("generic.lang_back_homepage") }}</span>
      </v-btn>

      <div :class="$vuetify.breakpoint.smAndDown ? '' : 'px-8'">
        <div class="pb-6 pt-5">
          <v-img
            :src="this.pdfData.logoImage"
            max-width="140"
            style="width: fit-content;"
          ></v-img>
        </div>

        <div class="d-flex justify-space-between">
          <div>
            <span class="font-weight-medium">
              {{ this.pdfData.company ? this.pdfData.company : "" }}
            </span>
            <br />
            <span v-text="this.pdfData.street"></span>
            <br />
            <span v-text="this.pdfData.zip_code + ' ' + this.pdfData.city">
            </span>
          </div>

          <div v-if="$vuetify.breakpoint.smAndUp">
            <span v-if="!!PostingDate">
              {{ $t("generic.lang_posting_date") }} {{ PostingDate }}
              <br />
            </span>
            <span v-if="!!datum">
              {{ $t("generic.lang_reservation_date") }} {{ datum }}
            </span>
          </div>

          <div v-else>
            <span v-if="!!datum">
              {{ $t("generic.lang_reservation_date") }}
              <br />
              {{ datum }}
            </span>
          </div>
        </div>

        <br />
        <br />
        <h2>
          {{
            isConfirmed
              ? $t("generic.lang_reservation_confirmation")
              : $t("generic.lang_reservation_request")
          }}
        </h2>
        <br />

        <div>
          <span class="font-weight-bold">
            {{ $t("generic.lang_hello_mr") }}
            {{ this.pdfData.salutation }}
            {{ this.pdfData.customer && this.pdfData.customer.first_name }}
            {{ this.pdfData.customer && this.pdfData.customer.last_name }}
          </span>
          <br />
          <span>
            {{
              isConfirmed
                ? $t("generic.lang_confirmation_text")
                : $t("generic.lang_reservation_request_text")
            }}
          </span>
        </div>
      </div>

      <!----------------------------------------------->
      <v-sheet
        elevation="0"
        color="grey lighten-3"
        class="rounded-lg my-8 pa-6"
      >
        <v-row no-gutters>
          <v-col cols="12" xs="12" sm="6">
            <div
              :class="
                Array.isArray(this.pdfData.items) &&
                !this.pdfData.items.length &&
                $vuetify.breakpoint.xsOnly
                  ? 'text-center mb-5'
                  : ''
              "
            >
              <h4 class="font-weight-bold mb-3">
                {{ $t("generic.lang_booking_info") }}
              </h4>

              <span>
                {{ $t("generic.lang_name") }}
                <span class="indigo--text">
                  {{
                    this.pdfData.customer && this.pdfData.customer.first_name
                  }}
                  {{ this.pdfData.customer && this.pdfData.customer.last_name }}
                </span>
              </span>

              <br />

              <span>
                {{ $t("generic.lang_date") }}
                <span class="indigo--text" v-text="this.datum" />
              </span>

              <br />

              <span>
                {{ $t("generic.lang_time") }}
                <span
                  class="indigo--text"
                  v-text="this.pdfData.time"
                />
              </span>

              <br />

              <span>
                {{ $t("generic.lang_number_people") }}
                <span
                  class="indigo--text"
                  v-text="this.pdfData.reservationPerson"
                />
              </span>

              <div
                v-if="
                  isConfirmed &&
                    Array.isArray(this.pdfData.items) &&
                    !this.pdfData.items.length &&
                    $vuetify.breakpoint.smAndUp
                "
              >
                <v-btn
                  dark
                  color="indigo"
                  elevation="0"
                  class="text-capitalize px-8 mt-4"
                  @click="downloadAndPrintPDF('download')"
                >
                  {{ $t("generic.lang_download_pdf") }}
                </v-btn>

                <br />

                <v-btn
                  dark
                  color="indigo"
                  elevation="0"
                  class="text-capitalize px-8 mt-4"
                  @click="downloadAndPrintPDF('print')"
                >
                  {{ $t("generic.lang_print") }}
                </v-btn>
              </div>
            </div>

            <div
              v-if="
                Array.isArray(this.pdfData.items) && this.pdfData.items.length
              "
              :class="$vuetify.breakpoint.xsOnly ? 'my-5' : 'mt-5'"
            >
              <div class="font-weight-medium">
                {{ $t("generic.lang_items") }}
              </div>

              <div
                v-for="(item, i) in this.pdfData.items"
                :key="i"
                class="d-flex align-start my-3"
              >
                <v-icon small> fa-{{ reservationTypeIcon }} </v-icon>

                <span class="ml-4 body-2">
                  {{ item.quantity + " * " + item.name }}
                </span>
              </div>

              <v-divider class="mt-5 mb-3"></v-divider>

              <div>
                <p class="font-weight-medium">
                  {{ $t("generic.lang_payable_amount") }}
                </p>

                <div class="d-flex flex-wrap justify-space-between">
                  <p>{{ $t("generic.lang_subtotal") }}</p>
                  <p>
                    {{ this.pdfData.total | currency }}
                  </p>
                </div>

                <div
                  v-for="(tax, i) in this.pdfData.taxes"
                  :key="i"
                  class="d-flex flex-wrap justify-space-between"
                >
                  <p>{{ tax.name }}</p>
                  <p>
                    {{ tax.total | currency }}
                  </p>
                </div>

                <div
                  class="d-flex flex-wrap justify-space-between font-weight-medium"
                >
                  <p>{{ $t("generic.lang_total_amount") }}</p>
                  <p>
                    {{ this.pdfData.totalWithTax | currency }}
                  </p>
                </div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" xs="12" sm="6">
            <div
              :class="$vuetify.breakpoint.xsOnly ? 'mx-auto' : 'float-right'"
              class="justify-center text-center"
              style="width: fit-content;"
            >
              <img
                class="white rounded-lg pa-2"
                :src="this.pdfData.imageQr"
                alt=""
                width="160"
              />

              <br />

              <div
                v-if="isConfirmed && $vuetify.breakpoint.smAndUp"
                class="caption text-center mx-auto"
                style="width: 210px;"
              >
                {{ $t("generic.lang_scan_qr") }}
              </div>

              <v-sheet
                v-if="!isConfirmed"
                elevation="0"
                color="indigo lighten-5"
                class="rounded-lg pa-4 mx-auto mt-3"
                style="max-width: 210px;"
              >
                <v-icon color="indigo"> mdi-clock-outline </v-icon>

                <div class="caption indigo--text text-center mt-2">
                  {{ $t("generic.lang_reservation_pending") }}
                </div>
              </v-sheet>

              <div v-if="isConfirmed && $vuetify.breakpoint.xsOnly">
                <v-btn
                  dark
                  color="indigo"
                  elevation="0"
                  class="text-capitalize px-8 mt-4"
                  @click="downloadICal()"
                >
                  {{ $t("generic.lang_download_ical") }}
                </v-btn>

                <br />
              </div>

              <div
                v-if="
                  isConfirmed &&
                    ((Array.isArray(this.pdfData.items) &&
                      this.pdfData.items.length) ||
                      $vuetify.breakpoint.xsOnly)
                "
              >
                <v-btn
                  dark
                  color="indigo"
                  elevation="0"
                  class="text-capitalize px-8 mt-4"
                  @click="downloadAndPrintPDF('download')"
                >
                  {{ $t("generic.lang_download_pdf") }}
                </v-btn>

                <br />

                <v-btn
                  v-if="$vuetify.breakpoint.smAndUp"
                  dark
                  color="indigo"
                  elevation="0"
                  class="text-capitalize px-8 mt-4"
                  @click="downloadAndPrintPDF('print')"
                >
                  {{ $t("generic.lang_print") }}
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-sheet>
      <!----------------------------------------------->

      <div :class="$vuetify.breakpoint.smAndDown ? '' : 'px-8'">
        <p>
          {{ $t("generic.lang_pleasant_stay") }}
        </p>
        <p>{{ $t("generic.lang_kind_regards") }}</p>
        <p class="font-weight-medium">
          {{ $t("generic.lang_team") + " " + this.pdfData.company }}
        </p>

        <v-divider width="300px" class="justify-center mx-auto mt-15 mb-2" />

        <p class="text-center mb-12">
          {{
            isConfirmed
              ? $t("generic.lang_letter_binding")
              : $t("generic.lang_letter_not_binding")
          }}
        </p>
        <h6 class="text-center black--text">
          {{ $t("generic.lang_reciept_copy_right") }}
        </h6>
      </div>
    </v-card>
  </v-container>
  <!--end booking receipt page-->
</template>

<script>
/*import plugins*/
import moment from "moment";
import ENDPOINTS from "@/plugins/axios/endpoints";

export default {
  name: "BookingReceipt",

  data() {
    return {
      isLoading: true,
      pdfData: [],
      orderUuid:
        localStorage.getItem("orderUuid") ||
        new URLSearchParams(window.location.search).get("reservation-uuid") ||
        "",
    };
  },

  computed: {
    // A reservation booked while automatic confirmation is off is only a request
    // until the restaurant confirms it in the POS. "confirmedByDefaulT" is the
    // reservation type switch in the POS, "confirmed" what the API decided for
    // this reservation. Missing flags keep the old behaviour: confirmed.
    isConfirmed() {
      const flag =
        this.pdfData.confirmedByDefault ??
        this.pdfData.reservationType?.confirmedByDefaulT ??
        this.pdfData.confirmed ??
        true;

      // tinyint columns reach us as 0/1 or "0"/"1"
      return !(flag === false || flag === 0 || flag === "0" || flag === "false");
    },
    PostingDate() {
      const d = this.pdfData.created_at;
      if (!d || d === "0") return null;

      const formats = ["DD.MM.YYYY", "MM.DD.YYYY"];
      const m = moment(d, formats, true);
      if (!m.isValid()) return null;

      return m.format("DD.MM.YYYY");
    },
    datum() {
      const d = this.pdfData.date;
      if (!d || d === "0") return null;

      const formats = ["DD.MM.YYYY", "MM.DD.YYYY"];
      const m = moment(d, formats, true);
      if (!m.isValid()) return null;

      return m.format("DD.MM.YYYY");
    },
    icalDTSTART() {
      const dateTime = moment(`${this.pdfData.date} ${this.pdfData.time}`,
        "DD.MM.YYYY HH:mm"
      );
      return dateTime.format("YYYYMMDDTHHmmss");
    },
    icalDTEND() {
      const dateTime = moment(`${this.pdfData.date} ${this.pdfData.time}`,
        "DD.MM.YYYY HH:mm"
      );
      const maxtime = this.pdfData.reservationType?.maxtime || 0;
      return dateTime.add(maxtime, "seconds").format("YYYYMMDDTHHmmss");
    },
    reservationTypeIcon() {
      return this.pdfData.reservationType && this.pdfData.reservationType.icon;
    },
    ical() {
      return `
        BEGIN:VCALENDAR
        VERSION:2.0
        BEGIN:VEVENT
        DTSTART:${this.icalDTSTART}
        DTEND:${this.icalDTEND}
        LOCATION:${this.pdfData.street}, ${this.pdfData.city} ${this.pdfData.zip_code}, ${this.pdfData.country}
        SUMMARY:${this.pdfData.company} ${this.$t("generic.lang_receiptName")}
        DESCRIPTION:${this.$t("generic.lang_number_people")} ${this.pdfData.reservationPerson}\\n${this.$t("generic.lang_Reservation_type")} ${this.pdfData.reservationType.name}
        END:VEVENT
        END:VCALENDAR
      `.replace(/^\s+|\s+$/gm, "");
    },
  },

  beforeRouteLeave() {
    localStorage.removeItem("orderUuid");
    window.location.reload();
  },

  mounted() {
    this.fetchPdfData();
  },

  methods: {
    backToHome() {
      localStorage.removeItem("orderUuid");
      window.location = "/";
    },
    download(filename, urlPdf) {
      var link = document.createElement("a");
      link.setAttribute("href", urlPdf);
      link.setAttribute("download", filename);
      link.style.display = "none";
      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
    },
    PrintPdf(pdf) {
      var iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdf;
      document.body.appendChild(iframe);

      // Use onload to make pdf preview work on firefox
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };
    },
    downloadICal() {
      let filename = `${this.$t("generic.lang_receiptName")}.ics`;
      let icalEncoded = encodeURIComponent(this.ical);
      let content = "data:text/calendar;charset=utf-8," + icalEncoded;

      this.download(filename, content);
    },
    async downloadAndPrintPDF(val) {
      await window
        .axios({
          method: "Get",
          url: `${ENDPOINTS.ONLINE_SERVICES.ORDER_PDF.GET_ALL}/${this.orderUuid}`,
          params: {
            action: 1,
          },
          responseType: "blob",
        })
        .then((response) => {
          const newBlob = new Blob([response.data], {
            type: "application/pdf",
          });

          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(newBlob);
            return;
          }

          var urlPdf = window.URL.createObjectURL(newBlob);

          if (val == "download") {
            this.download(`${this.$t("generic.lang_receiptName")}.pdf`, urlPdf);
          } else {
            this.PrintPdf(urlPdf);
          }

          window.URL.revokeObjectURL(urlPdf);
        })
        .catch((error) => {
          this.$swal({
            title: this.$t("generic.lang_error"),
            icon: "error",
            text: error.message,
          });
        })
        .finally(function() {});
    },

    async fetchPdfData() {
      let self = this;
      self.isLoading = true;

      await window
        .axios({
          method: "Get",
          url: `${ENDPOINTS.ONLINE_SERVICES.ORDER_PDF.GET_ALL}/${this.orderUuid}`,
          params: {
            action: 0,
          },
        })
        .then((response) => {
          this.pdfData = response.data;
        })
        .catch(() => {
          this.$router.push("/");
          self.isLoading = false;
        })
        .finally(function() {
          self.isLoading = false;
        });
    },
  },
};
</script>
